import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  archiveCharacter,
  createCharacter,
  getCharacters,
  getCharacterUsage,
  updateCharacter,
  restoreCharacter,
  permanentlyDeleteCharacter,
} from "../service/service";

export default function CharacterLibrary({ onChange }) {
  const [characters, setCharacters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState(null);
  const [head, setHead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const [notice, setNotice] = useState("");
  const [status, setStatus] = useState("active");
  const [archiving, setArchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const currentRequest = useRef(null);
  const changeCallback = useRef(onChange);
  changeCallback.current = onChange;
  const refresh = useCallback(async () => {
    const request = {};
    currentRequest.current = request;
    const result = await getCharacters(status);
    if (request !== currentRequest.current) return;
    setCharacters(result.data || []);
    changeCallback.current?.(result.data || []);
  }, [status]);
  useEffect(() => {
    setCharacters([]);
    refresh().catch(() => setError("Could not load characters."));
    return () => {
      currentRequest.current = null;
    };
  }, [refresh]);
  const report = (error) =>
    setError(
      error.response?.data?.err ||
        error.response?.data?.message ||
        error.message ||
        "Could not save character.",
    );
  const reset = () => {
    setEditing(null);
    setName("");
    setBody(null);
    setHead(null);
    setUsage(null);
  };
  const save = async (confirmed = false) => {
    setBusy(true);
    setError("");
    try {
      if (editing && (body || head) && !confirmed) {
        const result = await getCharacterUsage(editing._id);
        if (result.data?.length) {
          setUsage(result.data);
          return;
        }
      }
      const data = new FormData();
      data.append("displayName", name.trim());
      if (body) data.append("file", body);
      if (head) data.append("headFile", head);
      if (confirmed) data.append("confirmGlobalChange", "true");
      if (editing) await updateCharacter(editing._id, data);
      else await createCharacter(data);
      await refresh();
      reset();
      setNotice("Character saved. Artwork is shared across courses.");
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };
  const archive = async (character) => {
    setBusy(true);
    setError("");
    try {
      await archiveCharacter(character._id);
      setArchiving(null);
      if (editing?._id === character._id) reset();
      await refresh();
      setNotice("Character archived.");
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };
  const restore = async (character) => {
    setBusy(true);
    setError("");
    try {
      await restoreCharacter(character._id);
      await refresh();
      setNotice("Character restored.");
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };
  const removePermanently = async () => {
    setBusy(true);
    setError("");
    try {
      await permanentlyDeleteCharacter(deleting._id);
      setDeleting(null);
      await refresh();
      setNotice("Character and its Azure artwork permanently deleted.");
    } catch (error) {
      report(error);
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Typography variant="h6">Shared characters</Typography>
      <Typography color="text.secondary">
        Upload reusable PNG artwork, up to 5 MB per image. Character names are
        independent of voice names. Set colours in each course.
      </Typography>
      <FormControl size="small" sx={{ mt: 2, minWidth: 180 }}>
        <InputLabel id="character-status-label">Status</InputLabel>
        <Select
          labelId="character-status-label"
          label="Status"
          value={status}
          disabled={busy}
          onChange={(event) => {
            setStatus(event.target.value);
            reset();
            setNotice("");
            setError("");
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </Select>
      </FormControl>
      <List>
        {characters.map((character) => (
          <ListItem
            key={character._id}
            secondaryAction={
              <Stack direction="row">
                {!character.isArchived && (
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setEditing(character);
                      setName(character.displayName);
                      setBody(null);
                      setHead(null);
                      setUsage(null);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {!character.isArchived && (
                  <Button
                    disabled={busy}
                    onClick={() => setArchiving(character)}
                  >
                    Archive
                  </Button>
                )}
                {character.isArchived && (
                  <>
                    <Button
                      disabled={busy || Boolean(character.deletionStartedAt)}
                      onClick={() => restore(character)}
                    >
                      Restore
                    </Button>
                    <Button
                      color="error"
                      disabled={busy}
                      onClick={() => setDeleting(character)}
                    >
                      Delete permanently
                    </Button>
                  </>
                )}
              </Stack>
            }
          >
            <ListItemAvatar>
              <Avatar
                src={character.headUrl || character.characterUrl}
                variant="rounded"
                sx={{
                  bgcolor: "#F0FFF0",
                  width: 48,
                  height: 48,
                  mr: 1,
                  "& img": { objectFit: "contain" },
                }}
              />
            </ListItemAvatar>
            <ListItemText
              disableTypography
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{character.displayName}</span>
                  <Chip
                    size="small"
                    label={character.isArchived ? "Archived" : "Active"}
                    color={character.isArchived ? "default" : "success"}
                  />
                </Stack>
              }
              secondary={
                character.deletionStartedAt
                  ? "Deletion incomplete — retry permanent deletion"
                  : undefined
              }
            />
          </ListItem>
        ))}
      </List>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <Stack
          spacing={2}
          direction={{ xs: "column", md: "row" }}
          sx={{ alignItems: "center" }}
        >
          <TextField
            required
            label="Character name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 120 }}
          />
          <Button component="label" variant="outlined">
            {body?.name || "Full-body PNG"}
            <input
              hidden
              type="file"
              accept="image/png"
              onChange={(event) => setBody(event.target.files?.[0] || null)}
            />
          </Button>
          <Button component="label" variant="outlined">
            {head?.name || "Head PNG (optional)"}
            <input
              hidden
              type="file"
              accept="image/png"
              onChange={(event) => setHead(event.target.files?.[0] || null)}
            />
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !name.trim() || (!editing && !body)}
          >
            {editing ? "Save changes" : "Add character"}
          </Button>
          {editing && (
            <Button onClick={reset} disabled={busy}>
              Cancel edit
            </Button>
          )}
        </Stack>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {notice}
        </Alert>
      )}
      <Dialog open={Boolean(usage)} onClose={() => !busy && setUsage(null)}>
        <DialogTitle>Replace shared artwork?</DialogTitle>
        <DialogContent>
          <Typography>This change affects all of these courses:</Typography>
          <List>
            {usage?.map((course) => (
              <ListItem key={course._id}>
                <ListItemText
                  primary={course.name}
                  secondary={course.voices.join(", ")}
                />
              </ListItem>
            ))}
          </List>
          <Typography>
            Course colours and voice recordings will stay the same.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setUsage(null)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={() => save(true)}>
            Replace artwork globally
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(archiving)}
        onClose={() => !busy && setArchiving(null)}
      >
        <DialogTitle>Archive character?</DialogTitle>
        <DialogContent>
          <Typography>
            Courses already using {archiving?.displayName} will keep their
            character and artwork. Archiving only hides this character from the
            selection list for new assignments. You can restore it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setArchiving(null)}>
            Cancel
          </Button>
          <Button color="error" disabled={busy} onClick={() => archive(archiving)}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleting)}
        onClose={() => !busy && setDeleting(null)}
      >
        <DialogTitle>Delete character permanently?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {deleting?.displayName} and its artwork from Azure? This
            cannot be undone. Its character assignments will be removed from all
            courses, and the app will use the default Listening pose. Courses,
            voices and recordings will be kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button color="error" disabled={busy} onClick={removePermanently}>
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
